<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="products-page">
    <!-- HERO -->
    <section class="page-hero">
      <div class="page-hero-inner">
        <div class="section-label">Curated Collection</div>
        <h1 class="page-title">嚴選療癒植栽</h1>
        <p class="page-sub">每一株植物，都經過嚴格篩選，確保品質與低維護特性</p>
      </div>
    </section>

    <!-- PRODUCTS -->
    <section class="products-section">
      <div class="products-inner">
        <!-- 載入中 -->
        <div v-if="loading" class="loading-wrap">
          <div class="loading-spinner"></div>
          <p>載入商品中...</p>
        </div>

        <!-- 商品列表 -->
        <div v-else-if="products.length > 0" class="product-grid">
          <div v-for="p in products" :key="p.id" class="product-card">
            <div class="product-img">
              <span v-if="p.tag" class="product-tag">{{ p.tag }}</span>
              <!-- 有圖片就顯示圖片，沒有就顯示 emoji -->
              <img
                v-if="p.imageUrl"
                :src="p.imageUrl"
                :alt="p.name"
                class="product-image"
              />
              <span v-else class="product-emoji">{{ p.emoji }}</span>
            </div>
            <div class="product-info">
              <div class="product-latin">{{ p.latinName }}</div>
              <div class="product-name">{{ p.name }}</div>
              <div class="product-caption">{{ p.caption }}</div>
              <p class="product-desc">{{ p.description }}</p>
              <div class="product-footer">
                <div class="product-price">
                  NT$ {{ p.price.toLocaleString() }}
                </div>
                <button class="btn-inquiry" @click="inquiry(p)">
                  詢問購買
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 無商品 -->
        <div v-else class="no-data">
          <p>暫無商品資料</p>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="products-cta">
      <div class="cta-inner">
        <h2>找不到喜歡的？</h2>
        <p>歡迎聯絡我們，我們提供客製化植栽服務</p>
        <router-link to="/contact" class="btn-primary">聯絡我們</router-link>
      </div>
    </section>

    <!-- FOOTER -->
    <footer class="footer">
      <div class="footer-inner">
        <p>© 2026 綠憩植研 Green Rich Geam. All rights reserved.</p>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";

const router = useRouter();
const products = ref([]);
const loading = ref(true);

const loadProducts = async () => {
  try {
    const response = await axios.get("/products");
    products.value = response.data;
  } catch (error) {
    console.error("載入商品失敗:", error);
  } finally {
    loading.value = false;
  }
};

const inquiry = (product) => {
  router.push({
    path: "/contact",
    query: { product: product.name },
  });
};

onMounted(() => {
  loadProducts();
});
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.products-page {
  font-family: "Noto Sans TC", "Microsoft JhengHei", sans-serif;
  background: #f7f5ef;
  color: #1c2b1a;
}

/* HERO */
.page-hero {
  background: linear-gradient(135deg, #2d5016 0%, #4e7b4f 100%);
  color: white;
  padding: 80px 40px;
  text-align: center;
}

.page-hero-inner {
  max-width: 600px;
  margin: 0 auto;
}

.section-label {
  font-size: 12px;
  letter-spacing: 3px;
  text-transform: uppercase;
  opacity: 0.7;
  margin-bottom: 16px;
}

.page-title {
  font-size: 3em;
  font-weight: 600;
  margin-bottom: 16px;
}

.page-sub {
  font-size: 1.1em;
  opacity: 0.85;
  line-height: 1.7;
}

/* PRODUCTS */
.products-section {
  max-width: 1200px;
  margin: 80px auto;
  padding: 0 40px;
}

.products-inner {
  width: 100%;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 30px;
}

.product-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s;
}

.product-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
}

.product-img {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #eaf2e0 0%, #f0ede4 100%);
  position: relative;
}

.product-tag {
  position: absolute;
  top: 12px;
  right: 12px;
  background: #ff6b6b;
  color: white;
  padding: 4px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.product-emoji {
  font-size: 80px;
}

.product-info {
  padding: 24px;
}

.product-latin {
  color: #9baa97;
  font-size: 12px;
  font-style: italic;
  margin-bottom: 8px;
}

.product-name {
  font-size: 20px;
  font-weight: 600;
  color: #1c2b1a;
  margin-bottom: 8px;
}

.product-caption {
  font-size: 13px;
  color: #4e7b4f;
  margin-bottom: 12px;
  font-weight: 500;
}

.product-desc {
  font-size: 14px;
  color: #6b7f68;
  line-height: 1.6;
  margin-bottom: 20px;
}

.product-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #eaf2e0;
  padding-top: 16px;
}

.product-price {
  font-size: 20px;
  font-weight: 700;
  color: #2d5016;
}

.btn-inquiry {
  padding: 8px 20px;
  background: #4e7b4f;
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-inquiry:hover {
  background: #2d5016;
  transform: translateY(-1px);
}

/* LOADING */
.loading-wrap {
  text-align: center;
  padding: 80px 20px;
  color: #6b7f68;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #eaf2e0;
  border-top: 3px solid #4e7b4f;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.no-data {
  text-align: center;
  padding: 80px 20px;
  color: #9baa97;
  font-size: 18px;
}

/* CTA */
.products-cta {
  background: #eaf2e0;
  padding: 80px 40px;
  text-align: center;
  margin-top: 40px;
}

.cta-inner {
  max-width: 500px;
  margin: 0 auto;
}

.cta-inner h2 {
  font-size: 2em;
  color: #1c2b1a;
  margin-bottom: 12px;
}

.cta-inner p {
  color: #6b7f68;
  margin-bottom: 30px;
  font-size: 16px;
}

.btn-primary {
  padding: 14px 40px;
  background: #4e7b4f;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s;
  display: inline-block;
  font-weight: 600;
}

.btn-primary:hover {
  background: #2d5016;
  transform: translateY(-2px);
}

/* FOOTER */
.footer {
  background: #1c2b1a;
  color: #9baa97;
  text-align: center;
  padding: 40px;
}

.footer-inner {
  max-width: 1200px;
  margin: 0 auto;
}

/* Responsive */
@media (max-width: 768px) {
  .page-title {
    font-size: 2em;
  }

  .products-section {
    margin: 40px auto;
    padding: 0 20px;
  }

  .product-grid {
    grid-template-columns: 1fr;
  }
  .product-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }

  .product-img {
    position: relative; /* 確保這行有 */
  }
}
</style>
